import { Component, EventEmitter, Input, Output, signal, inject, computed, Signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule, ModalModule } from '@coreui/angular';
import { BizEntityFunctionTypes, IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { ToastService } from '@optima-stride-ai/optima-components';
import { LibraryService } from '../../pages/libraries/library.service';
import { HeaderUtilityService } from '../../services/header-utility.service';
import { ThumbnailManagerService } from '../../services/thumbnail-manager.service';
import { CollaboraService } from '../../services/collabora.service';
import { FileUploadService } from '../../services/file-upload.service';
import { BizEntityTypeCategory } from '@optima-stride-ai/core';
import { hayagrivaAppServerURL } from '../../../environments/environment';
import { 
  FileCategoryUtils,
  UIMessages,
  LogMessages
} from '../../shared/enums/app-constants';
import openFilePicker from '../../../enum/file-picker';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [CommonModule, ButtonModule, ModalModule],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.sass']
})
export class FileUploadComponent {
  @Input() availableFolders!: Signal<IBizEntity[]>;
  @Input() showUploadDialog!: WritableSignal<boolean>;
  @Input() selectedUploadFolder!: WritableSignal<string | null>;
  
  @Output() uploadCompleted = new EventEmitter<{ folderId: string, uploadedFiles: any }>();
  @Output() uploadCancelled = new EventEmitter<void>();
  @Output() uploadStarted = new EventEmitter<void>();
  @Output() folderSelected = new EventEmitter<string>();

  // Internal state
  selectedFiles = signal<File[]>([]);
  isUploading = signal<boolean>(false);
  uploadProgress = signal<string>('');

  // Services
  private libraryService = inject(LibraryService);
  private headerUtilityService = inject(HeaderUtilityService);
  private thumbnailManagerService = inject(ThumbnailManagerService);
  private collaboraService = inject(CollaboraService);
  private fileUploadService = inject(FileUploadService);
  private logger = inject(LoggerService);
  private toastService = inject(ToastService);
  private appServerURL = hayagrivaAppServerURL;

  // Computed properties
  selectedFolderName = computed(() => {
    const folderId = this.selectedUploadFolder();
    if (!folderId) return '';
    const folder = this.availableFolders().find(f => f.id === folderId);
    return folder?.name || '';
  });

  /**
   * Open file picker for upload
   */
  async openFilePicker(): Promise<void> {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    
    // Determine the folder type from selectedUploadFolder
    const folderId = this.selectedUploadFolder();
    let type: any = null;
    if (folderId) {
      const folder = this.availableFolders().find(f => f.id === folderId);
      if (folder) {
        type = folder.type || folder;
      }
    }
    
    // Set file type filter based on category
    input.accept = openFilePicker.ANY;
    if (type && type.category == BizEntityTypeCategory.HayagrivaFilePDF) {
      input.accept = openFilePicker.PDF;
    }
    if (type && type.category == BizEntityTypeCategory.HayagrivaFileImage) {
      input.accept = openFilePicker.IMAGE;
    }
    if (type && type.category == BizEntityTypeCategory.HayagrivaFileMarkdown) {
      input.accept = openFilePicker.MARKDOWN;
    }
    if (type && type.category == BizEntityTypeCategory.HayagrivaFileVideo) {
      input.accept = openFilePicker.VIDEO;
    }
    if (type && type.category == BizEntityTypeCategory.HayagrivaFileDoc) {
      input.accept = openFilePicker.DOC;
    }
    if (type && type.category == BizEntityTypeCategory.HayagrivaFileSpreadsheet) {
      input.accept = openFilePicker.SPREADSHEET;
    }
    if( type && type.category == BizEntityTypeCategory.HayagrivaFileCSV) {
      input.accept = openFilePicker.CSV;
    }
    if (type && type.category == BizEntityTypeCategory.HayagrivaFilePresentation) {
      input.accept = openFilePicker.PRESENTATION;
    }
    if (type && type.category == BizEntityTypeCategory.HayagrivaFileDrawing) {
      input.accept = openFilePicker.DRAWING;
    }

    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const filesArray = Array.from(target.files);
        this.selectedFiles.set(filesArray);
        this.showUploadDialog.set(true);
      }
    };

    input.click();
  }

  /**
   * Open file picker for dynamic upload with automatic processing
   */
  async openFilePickerForDynamicUpload(targetItem: IBizEntity | any, selectedItem: any): Promise<void> {
    if (this.isUploading()) {
      this.logger.warn('⚠️ Upload already in progress, please wait...');
      this.toastService.toast('Upload is already in progress. Please wait for it to complete.');
      return;
    }

    // Check if the target folder is operational
    if (targetItem?.type?.functionType === BizEntityFunctionTypes.Operational) {
      this.logger.warn('⚠️ Uploads to operational folders are not allowed');
      this.toastService.toast(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    
    // Get type from targetItem
    const type = selectedItem;
    
    // Set file type filter based on category
    input.accept = openFilePicker.ANY;
    if (type.category == BizEntityTypeCategory.HayagrivaFilePDF) {
      input.accept = openFilePicker.PDF;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFileImage) {
      input.accept = openFilePicker.IMAGE;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFileMarkdown) {
      input.accept = openFilePicker.MARKDOWN;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFileVideo) {
      input.accept = openFilePicker.VIDEO;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFileDoc) {
      input.accept = openFilePicker.DOC;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFileSpreadsheet) {
      input.accept = openFilePicker.SPREADSHEET;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFileCSV) {
      input.accept = openFilePicker.CSV;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFilePresentation) {
      input.accept = openFilePicker.PRESENTATION;
    }
    if (type.category == BizEntityTypeCategory.HayagrivaFileDrawing) {
      input.accept = openFilePicker.DRAWING;
    }

    input.style.display = 'none';
    document.body.appendChild(input);

    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const filesArray = Array.from(target.files);
        
        // Validate file types
        const { valid: validFiles, invalid: invalidFiles } = this.validateFileTypes(filesArray, targetItem);
        
        if (invalidFiles.length > 0) {
          const allowedTypes = this.getAllowedFileTypesMessage(targetItem);
          this.toastService.toast(`Some files are not supported for this folder type. Only ${allowedTypes} are allowed.\n\nInvalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
          
          if (validFiles.length === 0) {
            document.body.removeChild(input);
            return;
          }
        }
        
        // Notify parent to start shimmer now that files have been selected
        this.uploadStarted.emit();

        // Proceed with dynamic upload for valid files
        await this.performDynamicUpload(targetItem.id, validFiles);
      }

      document.body.removeChild(input);
    };

    input.oncancel = () => {
      document.body.removeChild(input);
      this.uploadCancelled.emit();
    };

    input.click();
  }

  /**
   * Validate file types based on target folder category
   */
  private validateFileTypes(files: File[], targetItem: IBizEntity | any): { valid: File[], invalid: File[] } {
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    if (!targetItem?.category) {
      return { valid: files, invalid: [] };
    }

    files.forEach(file => {
      const fileName = file.name.toLowerCase();
      let isValid = false;

      switch (targetItem.category) {
        case BizEntityTypeCategory.HayagrivaFileImage:
          isValid = this.isImageFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFileVideo:
          isValid = this.isVideoFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFilePDF:
          isValid = this.isPdfFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFileMarkdown:
          isValid = this.isMarkdownFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFileDoc:
          isValid = this.isOfficeDocumentFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
          isValid = this.isSpreadsheetFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFileCSV:
          isValid = this.isCsvFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFilePresentation:
          isValid = this.isPresentationFile(fileName);
          break;
        case BizEntityTypeCategory.HayagrivaFileDrawing:
          isValid = this.isDiagramFile(fileName);
          break;
        default:
          isValid = true;
      }

      if (isValid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    return { valid: validFiles, invalid: invalidFiles };
  }

  /**
   * Perform upload
   */
  async performUpload(): Promise<void> {
    const folderId = this.selectedUploadFolder();
    const files = this.selectedFiles();

    if (!folderId || files.length === 0) {
      this.logger.warn('No folder or files selected');
      return;
    }

    // Check if the target folder is operational
    const folder = this.availableFolders().find(f => f.id === folderId);
    if (folder?.type?.functionType === BizEntityFunctionTypes.Operational) {
      this.logger.warn('⚠️ Uploads to operational folders are not allowed');
      this.toastService.toast(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
      return;
    }

    this.isUploading.set(true);
    this.uploadProgress.set(`Uploading ${files.length} file(s)...`);

    try {
      this.libraryService.uploadMultipleFiles(folderId, files).subscribe({
        next: async (uploadedFiles: any) => {
          this.logger.info(`✅ Upload successful: ${uploadedFiles.length} files uploaded`);
          this.toastService.toast(LogMessages.SUCCESS_UPLOAD_COMPLETE);
          this.uploadCompleted.emit({ folderId, uploadedFiles });
          this.closeDialog();
        },
        error: (error: any) => {
          this.logger.error(`❌ Upload failed: ${error}`);
          this.toastService.toast('Failed to upload files. Please try again.');
          this.isUploading.set(false);
          this.uploadProgress.set('');
        }
      });
    } catch (error) {
      this.logger.error(`❌ Upload error: ${error}`);
      this.toastService.toast('An unexpected error occurred during upload.');
      this.isUploading.set(false);
      this.uploadProgress.set('');
    }
  }

  /**
   * Perform dynamic upload with automatic processing
   */
  async performDynamicUpload(folderId: string, files: File[]): Promise<void> {
    if (!folderId || files.length === 0) {
      this.logger.warn('❌ No folder or files selected for dynamic upload');
      return;
    }

    try {
      this.isUploading.set(true);
      this.uploadProgress.set(`Uploading ${files.length} file(s)...`);

      // Use the FileUploadService to handle the upload
      const uploadObservable = await this.fileUploadService.performDynamicUpload(folderId, files);
      
      uploadObservable.subscribe({
        next: async (uploadedFiles: any) => {
          this.logger.info(`✅ Dynamic upload successful: ${uploadedFiles.length} files uploaded`);
          this.toastService.toast(LogMessages.SUCCESS_UPLOAD_COMPLETE);
          this.uploadCompleted.emit({ folderId, uploadedFiles });
          this.isUploading.set(false);
          this.uploadProgress.set('');
        },
        error: (error: any) => {
          this.logger.error(`❌ Dynamic upload failed: ${error}`);
          this.isUploading.set(false);
          this.uploadProgress.set('');
          this.uploadCancelled.emit();
          
          if (error.status === 413) {
            this.toastService.toast('File is too large. Please choose a smaller file (max 50MB).');
          } else if (error.status === 415) {
            this.toastService.toast('File type not supported. Please choose a different file.');
          } else {
            this.toastService.toast('Failed to upload files. Please try again.');
          }
        }
      });

    } catch (error) {
      this.logger.error(`❌ Dynamic upload error: ${error}`);
      this.selectedFiles.set([]);
      this.isUploading.set(false);
      this.uploadProgress.set('');
      this.uploadCancelled.emit();

      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage === UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD) {
        this.toastService.toast(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
        return;
      }

      this.toastService.toast('An unexpected error occurred during upload. Please try again.');
    }
  }

  /**
   * Generate thumbnail for a specific file
   */
  async generateThumbnailForFile(file: File): Promise<string | null> {
    try {
      const result = await this.thumbnailManagerService.generateThumbnail(file);
      if (result.dataUrl && !result.error) {
        return result.dataUrl;
      }
      return null;
    } catch (error) {
      this.logger.error(`❌ Thumbnail generation failed: ${error}`);
      return null;
    }
  }

  /**
   * Close dialog and reset state
   */
  closeDialog(): void {
    this.showUploadDialog.set(false);
    this.selectedFiles.set([]);
    this.isUploading.set(false);
    this.uploadProgress.set('');
    this.uploadCancelled.emit();
  }

  /**
   * Format file size in human-readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get allowed file types message based on category
   */
  private getAllowedFileTypesMessage(targetItem: IBizEntity | any): string {
    if (!targetItem?.category) {
      return 'all file types';
    }

    switch (targetItem.category) {
      case BizEntityTypeCategory.HayagrivaFileImage:
        return 'image files (JPG, PNG, GIF, WebP, SVG)';
      case BizEntityTypeCategory.HayagrivaFileVideo:
        return 'video files (MP4, AVI, MOV, WebM, MKV)';
      case BizEntityTypeCategory.HayagrivaFilePDF:
        return 'PDF files';
      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return 'Markdown files (.md, .markdown)';
      case BizEntityTypeCategory.HayagrivaFileDoc:
        return 'document files (DOC, DOCX, ODT, RTF)';
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
        return 'spreadsheet files (XLS, XLSX, ODS, CSV)';
      case BizEntityTypeCategory.HayagrivaFileCSV:
        return 'CSV files (.csv)';
      case BizEntityTypeCategory.HayagrivaFilePresentation:
        return 'presentation files (PPT, PPTX, ODP)';
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return 'diagram files (ODG)';
      default:
        return 'all file types';
    }
  }

  // File type validation methods
  // private isImageFile(filename: string): boolean {
  //   return FileExtensionGroups.IMAGES.some(ext => filename.endsWith(ext));
  // }

    private isImageFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isImageCategory(category!);
  }

  // private isPdfFile(filename: string): boolean {
  //   return filename.endsWith(FileExtensions.PDF);
  // }
    private isPdfFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isPdfCategory(category!);
  }

  // private isVideoFile(filename: string): boolean {
  //   return FileExtensionGroups.VIDEOS.some(ext => filename.endsWith(ext));
  // }

  // private isMarkdownFile(filename: string): boolean {
  //   return filename.endsWith(FileExtensions.MD) || filename.endsWith(FileExtensions.MARKDOWN);
  // }

  // private isOfficeDocumentFile(filename: string): boolean {
  //   return FileExtensionGroups.DOCUMENTS.some(ext => filename.endsWith(ext));
  // }

  // private isSpreadsheetFile(filename: string): boolean {
  //   return FileExtensionGroups.SPREADSHEETS.some(ext => filename.endsWith(ext));
  // }

  // private isPresentationFile(filename: string): boolean {
  //   return FileExtensionGroups.PRESENTATIONS.some(ext => filename.endsWith(ext));
  // }

  // private isDiagramFile(filename: string): boolean {
  //   return filename.endsWith(FileExtensions.ODG);
  // }

    private isVideoFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isVideoCategory(category!);
  }

  private isMarkdownFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isMarkdownCategory(category!);
  }

  private isOfficeDocumentFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isDocumentCategory(category!);
  }

  private isSpreadsheetFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isSpreadsheetCategory(category!);
  }

  private isCsvFile(filename: string): boolean {
    return filename.endsWith('.csv');
  }

  private isPresentationFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isPresentationCategory(category!);
  }

  private isDiagramFile(filename: string): boolean {
    const category = FileCategoryUtils.getCategoryFromFilename(filename);
    return FileCategoryUtils.isDrawingCategory(category!);
  }
}